// Single source of truth for the Partner & Advocate Playbook / Field Guide.
// Consumed by:
//   - src/pages/PartnerHandbook.tsx (auth-gated full handbook)
//   - src/pages/PartnerPlaybookPublic.tsx (public preview microsite)
//   - scripts/build-partner-field-guide.ts (printable PDF)
//
// Tag every section with the tiers it applies to so the same content can be
// filtered on both surfaces without maintaining two copies.

export type PlaybookTier = "advocate" | "partner" | "anchor";

export type PlaybookBlock =
  | { type: "p"; text: string }
  | { type: "list"; items: string[] }
  | { type: "script"; label: string; body: string }
  | { type: "table"; caption?: string; headers: string[]; rows: string[][] }
  | { type: "callout"; tone?: "info" | "warn"; title?: string; text: string };

export type PlaybookSection = {
  id: string;
  title: string;
  tiers: PlaybookTier[];
  publicPreview?: boolean;
  updatedAt: string; // ISO date
  version: string;
  blocks: PlaybookBlock[];
};

export const PLAYBOOK_VERSION = "2026.07";

export const TIER_LABEL: Record<PlaybookTier, string> = {
  advocate: "Advocate",
  partner: "Partner",
  anchor: "Anchor",
};

export const PLAYBOOK_SECTIONS: PlaybookSection[] = [
  {
    id: "overview",
    title: "1. Program overview",
    tiers: ["advocate", "partner", "anchor"],
    publicPreview: true,
    updatedAt: "2026-07-10",
    version: PLAYBOOK_VERSION,
    blocks: [
      { type: "p", text: "The Emerging Market Partner Program is how vetted service providers, advocates, and anchor institutions extend our reach so Alabama-grown ideas get the legal, financial, technical, and distribution support they need to ship." },
      { type: "p", text: "There are three tiers — Advocate, Partner, and Anchor — each with its own commitments and benefits. Most organizations start as a Partner." },
      {
        type: "table",
        headers: ["Tier", "Who it fits", "Core commitment"],
        rows: [
          ["Advocate", "Referrers, amplifiers, community connectors", "Share the platform, submit quality referrals"],
          ["Partner", "Vetted service providers (legal, capital, build, distribution, mentorship, ops)", "Respond to matched ideas within SLA, file quarterly outcomes"],
          ["Anchor", "Strategic anchor institutions (chambers, universities, funders)", "Named MOU, co-branded landing page, quarterly stewardship review"],
        ],
      },
    ],
  },
  {
    id: "conduct",
    title: "2. Code of conduct",
    tiers: ["advocate", "partner", "anchor"],
    publicPreview: true,
    updatedAt: "2026-07-10",
    version: PLAYBOOK_VERSION,
    blocks: [
      { type: "list", items: [
        "Center the community. Every engagement is in service of the idea author and their community, not your pipeline.",
        "Honor confidentiality. Ideas are the author's IP. You may not act on, replicate, or share an idea without the author's written acceptance.",
        "Be honest about scope and fit. If you can't help, decline clearly so the platform can route elsewhere.",
        "No predatory pricing or unsolicited upsells on routed leads.",
      ]},
    ],
  },
  {
    id: "engage-ideas",
    title: "3. Engaging with ideas",
    tiers: ["partner", "anchor"],
    updatedAt: "2026-07-10",
    version: PLAYBOOK_VERSION,
    blocks: [
      { type: "p", text: "Express Interest — Browse published ideas in the Ideas Board or your Partner dashboard. When you can offer concrete help (legal, capital, build, distribution, mentorship), click Express Interest, pick the help kind, and send a short message. The idea author is notified and can accept or decline." },
      { type: "p", text: "Get Matched — Idea authors can also Request a Partner Match. We surface qualified partners by service category and county overlap. Admins may confirm or route the match." },
      { type: "p", text: "Engagement flow: Pending → Accepted → In-progress → Completed. Authors mark completion; we use these signals for outcomes reporting." },
    ],
  },
  {
    id: "sla",
    title: "4. Response SLAs",
    tiers: ["partner", "anchor"],
    updatedAt: "2026-07-10",
    version: PLAYBOOK_VERSION,
    blocks: [
      { type: "list", items: [
        "Partners: Respond to accepted match requests within 3 business days. Respond to author messages within 5 business days.",
        "Anchors: Quarterly stewardship review with our team plus monthly check-ins during active programs.",
        "Repeated SLA misses trigger a check-in, then a tier review.",
      ]},
    ],
  },
  {
    id: "brand",
    title: "5. Brand & messaging",
    tiers: ["advocate", "partner", "anchor"],
    publicPreview: true,
    updatedAt: "2026-07-10",
    version: PLAYBOOK_VERSION,
    blocks: [
      { type: "list", items: [
        "Use 'in partnership with The Emerging Market' or 'a partner of The Emerging Market.' Don't claim endorsement of a specific outcome.",
        "Logos, badges, and approved copy are in your portal under Resources.",
        "When promoting referred content, disclose your role as Partner or Advocate.",
      ]},
      {
        type: "callout",
        tone: "info",
        title: "Voice",
        text: "We speak as an Alabama government affairs firm serving small, community-rooted organizations. Coalition-neutral language — never 'minority-led' or 'DBE' on public marketing surfaces.",
      },
    ],
  },
  {
    id: "reporting",
    title: "6. Reporting & outcomes",
    tiers: ["partner", "anchor"],
    updatedAt: "2026-07-10",
    version: PLAYBOOK_VERSION,
    blocks: [
      { type: "list", items: [
        "Quarterly partner report: ideas engaged, scopes proposed, projects shipped, dollars deployed, and lessons learned.",
        "Reports power our public ecosystem dashboards (aggregated, no PII).",
        "Anchors receive bespoke impact dashboards tied to their program commitments.",
      ]},
    ],
  },
  {
    id: "payouts",
    title: "7. Payouts & revenue share",
    tiers: ["advocate", "partner"],
    updatedAt: "2026-07-10",
    version: PLAYBOOK_VERSION,
    blocks: [
      { type: "list", items: [
        "Affiliate referral payouts are display-only today; we'll turn on payout rails once compliance is in place.",
        "Revenue share applies to referred upgrades (e.g., new Pro subscriptions) at standard published rates (20% for 12 months by default).",
        "Partner-delivered work is billed by the partner directly to the idea author or sponsor; The Emerging Market does not take a cut on partner-delivered services in this phase.",
      ]},
    ],
  },
  {
    id: "escalation",
    title: "8. Escalation & disputes",
    tiers: ["advocate", "partner", "anchor"],
    updatedAt: "2026-07-10",
    version: PLAYBOOK_VERSION,
    blocks: [
      { type: "list", items: [
        "First step: contact the idea author directly through the engagement thread.",
        "Unresolved within 5 business days: email program@emrkt.co with the engagement ID.",
        "Tier offboarding is a last resort and follows a 14-day notice and a written review.",
      ]},
    ],
  },
  {
    id: "lifecycle",
    title: "9. Lifecycle: application → renewal",
    tiers: ["advocate", "partner", "anchor"],
    updatedAt: "2026-07-10",
    version: PLAYBOOK_VERSION,
    blocks: [
      { type: "list", items: [
        "Apply → Admin review (≤ 5 business days) → Onboarding call → Active.",
        "Tier review every 12 months. Promotion or change in tier is based on outcomes, SLA, and community feedback.",
        "Offboarding: notice, final report, deactivation of profile, and a public thank-you when appropriate.",
      ]},
    ],
  },
  {
    id: "faq",
    title: "10. Quick FAQ",
    tiers: ["advocate", "partner", "anchor"],
    updatedAt: "2026-07-10",
    version: PLAYBOOK_VERSION,
    blocks: [
      { type: "list", items: [
        "Q: Can I be both an Advocate and a Partner? A: Yes — many partners refer too. You'll get one referral code tied to your org.",
        "Q: Can I see who else has expressed interest on an idea? A: Only if the idea author shares that visibility. Default is private.",
        "Q: How do I update my services or counties? A: Portal → Settings → Org profile.",
      ]},
    ],
  },

  // ─── Appendices ──────────────────────────────────────────────────────────

  {
    id: "scripts",
    title: "A. Referral scripts & social kits",
    tiers: ["advocate", "partner"],
    publicPreview: true,
    updatedAt: "2026-07-10",
    version: PLAYBOOK_VERSION,
    blocks: [
      { type: "p", text: "Copy-paste starters. Swap {{firstName}}, {{org}}, and your {{referralLink}} (find yours in Portal → Payouts)." },
      {
        type: "script",
        label: "Email — warm intro",
        body: `Subject: A tool for {{org}} — Alabama funding + policy in one place\n\nHi {{firstName}},\n\nQuick share: The Emerging Market is a government affairs firm in Mobile that built a live workspace for Alabama community organizations — grant finder, legislation tracker, county-level data, and a partner network.\n\nGiven what {{org}} is working on, I think the Grant Finder and Legislation Tracker alone will pay for themselves. It's free to start.\n\n{{referralLink}}\n\nHappy to walk you through it if useful.`,
      },
      {
        type: "script",
        label: "LinkedIn DM",
        body: `Hey {{firstName}} — sharing a resource I've been using: The Emerging Market (emrkt.co). It's an Alabama-focused workspace for grants, bills, and community data. Free to try. If {{org}} touches any of that, worth 5 minutes: {{referralLink}}`,
      },
      {
        type: "script",
        label: "SMS / text",
        body: `{{firstName}} — check this out when you have a sec, built for Alabama nonprofits and small orgs: {{referralLink}}. Free account. Grants + bill tracking in one place.`,
      },
      {
        type: "script",
        label: "Event 30-second pitch",
        body: `We work with a firm called The Emerging Market — they built a tool that pulls every Alabama grant, every state bill, and county-level data into one dashboard. If you're chasing funding or trying to figure out what's moving in Montgomery, it's the first place I'd send you. It's emrkt.co, and I can share my referral link.`,
      },
      {
        type: "script",
        label: "LinkedIn post",
        body: `Alabama nonprofits, chambers, and small shops — if you're spending hours hunting grants or tracking legislation, look at @The Emerging Market.\n\nOne dashboard: live grant finder, AL bill tracker, county data, and a partner network of vetted service providers.\n\nProud to partner. Try it free → {{referralLink}}`,
      },
      {
        type: "script",
        label: "X / short post",
        body: `Alabama funding + policy, one workspace. @emrkt_co built the tool small orgs have been asking for. Free to start → {{referralLink}}`,
      },
      {
        type: "script",
        label: "Instagram caption",
        body: `We partner with @emrkt.co — an Alabama government affairs firm powering the work that moves communities forward. Grants, bills, data, and a partner network in one place. Link in bio.`,
      },
      {
        type: "callout",
        tone: "info",
        title: "Brand assets",
        text: "Grab logo lockups, partner/anchor badge PNGs, and boilerplate copy from Portal → Resources. Always disclose your role ('Partner' or 'Advocate of The Emerging Market').",
      },
    ],
  },
  {
    id: "scoping",
    title: "B. Scoping & pricing guardrails",
    tiers: ["partner", "anchor"],
    updatedAt: "2026-07-10",
    version: PLAYBOOK_VERSION,
    blocks: [
      { type: "p", text: "A good scope names the problem, the deliverable, the timeline, and the success metric. Anything less turns into scope creep." },
      {
        type: "list",
        items: [
          "Problem — one sentence, from the community's point of view, not yours.",
          "Deliverable — a specific artifact or outcome the author will hold at the end.",
          "Timeline — start date, milestone dates, end date. Never open-ended.",
          "Success metric — one measurable thing (submitted grant, MVP live, contract signed, dollars raised).",
          "Assumptions — what has to be true for the price and timeline to hold.",
          "Out of scope — write this down. It's the most useful part.",
        ],
      },
      {
        type: "table",
        caption: "Fair pricing bands (guidance, not a floor or ceiling)",
        headers: ["Service", "Typical range", "Notes"],
        rows: [
          ["Grant proposal (single opportunity)", "$1,500 – $6,000", "Higher end for federal / >$500K asks."],
          ["Brand identity (small org)", "$2,500 – $8,000", "Logo, color, type, one-page brand doc."],
          ["MVP web build", "$6,000 – $25,000", "Scope-bound, 6–10 weeks. Not open-ended dev."],
          ["Fractional legal (formation + basics)", "$1,000 – $4,000 flat", "Entity, EIN, ops agreement, template contracts."],
          ["Market / feasibility research", "$3,500 – $12,000", "Deliverable is a decision, not a deck."],
          ["Fractional grant management", "$1,500 – $4,000 / mo", "3-mo minimum, monthly report to author."],
        ],
      },
      {
        type: "callout",
        tone: "warn",
        title: "Red flags — routed leads",
        text: "Retainer-only proposals with no deliverable, prices >2× the band without a written reason, or upsells on the same call as intake. Any of these can trigger a partner review.",
      },
      {
        type: "script",
        label: "One-page SOW template",
        body: `SCOPE OF WORK — {{Partner}} × {{IdeaAuthor}}\n\n1. Problem\n2. Deliverable\n3. Milestones & dates\n4. Success metric\n5. Fee & payment schedule\n6. Assumptions\n7. Out of scope\n8. Change-order process\n\nSigned: __________________  Date: __________`,
      },
    ],
  },
  {
    id: "outcomes",
    title: "C. Outcomes storytelling",
    tiers: ["partner", "anchor"],
    updatedAt: "2026-07-10",
    version: PLAYBOOK_VERSION,
    blocks: [
      { type: "p", text: "Your quarterly report is how we prove the program works — to funders, to policymakers, and to the next community you want to serve. Treat it as documentation, not paperwork." },
      {
        type: "list",
        items: [
          "Ideas engaged — count of ideas where you opened a thread or accepted a match.",
          "Scopes proposed — written SOWs delivered to authors, even if not signed.",
          "Projects shipped — deliverables actually completed and accepted.",
          "Dollars deployed — grant + contract dollars authors received where you materially helped.",
          "Highlights — 2–3 sentences on what worked. Names + counties if the author consents.",
          "Lessons — one thing that didn't work and what you changed.",
        ],
      },
      {
        type: "callout",
        tone: "info",
        title: "Testimonial & photo consent",
        text: "Before quoting an author or using their photo, get written consent. One-line release: 'I, {{name}}, grant The Emerging Market and {{partner}} the right to use my quote and image in published materials describing this engagement.'",
      },
      { type: "p", text: "Case studies — Insights welcomes 600–900 word writeups. Structure: (1) the community and the problem, (2) what we tried, (3) what shipped, (4) what we learned, (5) what's next. Email drafts to program@emrkt.co." },
      {
        type: "table",
        caption: "Metric definitions — keep numbers comparable",
        headers: ["Metric", "Counts", "Does not count"],
        rows: [
          ["Ideas engaged", "Threads opened, matches accepted", "Ideas you only viewed"],
          ["Scopes proposed", "Written SOW delivered", "Verbal quotes, unsent drafts"],
          ["Projects shipped", "Author-accepted deliverable", "In-progress, cancelled work"],
          ["Dollars deployed", "Grants awarded, contracts signed in the quarter", "Pipeline, applications submitted"],
        ],
      },
    ],
  },
];

export function filterByTier(sections: PlaybookSection[], tier: PlaybookTier | "all"): PlaybookSection[] {
  if (tier === "all") return sections;
  return sections.filter((s) => s.tiers.includes(tier));
}

export function publicPreviewSections(): PlaybookSection[] {
  return PLAYBOOK_SECTIONS.filter((s) => s.publicPreview);
}
